import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { exercisesApi, reportsApi, exportApi, clientsApi, subCompaniesApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Download,
  FileText,
  Target,
  Activity,
  MessageSquare,
  Clock,
  BarChart3,
  TrendingUp,
  Building2,
  Filter,
  PieChart
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F'];
const STATUS_COLORS = {
  draft: '#6b7280',
  planned: '#f59e0b',
  active: '#10b981',
  completed: '#3b82f6',
  disabled: '#ef4444'
};

export default function ReportsPage() {
  const [exercises, setExercises] = useState([]);
  const [clients, setClients] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedSubCompany, setSelectedSubCompany] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [hierarchicalData, setHierarchicalData] = useState(null);
  const [graphicalData, setGraphicalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [pdfExercisesOpen, setPdfExercisesOpen] = useState(false);
  const [pdfSelectedExerciseIds, setPdfSelectedExerciseIds] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadHierarchicalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, selectedSubCompany, selectedExercise]);

  useEffect(() => {
    if (selectedExercise && selectedExercise !== 'all_exercises') {
      loadReportData(selectedExercise);
    } else {
      setReportData(null);
    }
  }, [selectedExercise]);

  const loadInitialData = async () => {
    try {
      const [exercisesData, clientsData, subCompaniesData] = await Promise.all([
        exercisesApi.getAll(),
        clientsApi.getAll().catch(() => []),
        subCompaniesApi.getAll().catch(() => [])
      ]);
      setExercises(exercisesData);
      setClients(clientsData);
      setSubCompanies(subCompaniesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchicalData = async () => {
    try {
      const exerciseId = selectedExercise && selectedExercise !== 'all_exercises' ? selectedExercise : undefined;
      const [hierarchical, graphical] = await Promise.all([
        reportsApi.getHierarchical({ clientId: selectedClient, subCompanyId: selectedSubCompany, exerciseId }),
        reportsApi.getGraphicalData({ clientId: selectedClient, subCompanyId: selectedSubCompany, exerciseId })
      ]);
      setHierarchicalData(hierarchical);
      setGraphicalData(graphical);
    } catch (error) {
      console.error('Failed to load hierarchical data:', error);
    }
  };

  const loadReportData = async (exerciseId) => {
    setReportLoading(true);
    try {
      const data = await reportsApi.getExerciseReportJson(exerciseId);
      setReportData(data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedExercise || selectedExercise === 'all_exercises') return;
    try {
      const blob = await reportsApi.downloadExerciseReportPdf(selectedExercise);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exercise_report_${selectedExercise}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleDownloadHierarchicalPdf = async () => {
    try {
      const blob = await reportsApi.downloadHierarchicalPdf({
        clientId: selectedClient,
        subCompanyId: selectedSubCompany,
        exerciseIds: pdfSelectedExerciseIds
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hierarchical_report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleExportExercisesExcel = async () => {
    try {
      const blob = await exportApi.exercisesExcel({
        clientId: selectedClient !== 'all' ? selectedClient : undefined,
        subCompanyId: selectedSubCompany !== 'all' ? selectedSubCompany : undefined
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exercises_export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exercises exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExportInjectsExcel = async () => {
    try {
      const blob = await exportApi.injectsExcel({
        exerciseId: (selectedExercise && selectedExercise !== 'all_exercises') ? selectedExercise : undefined,
        clientId: selectedClient !== 'all' ? selectedClient : undefined,
        subCompanyId: selectedSubCompany !== 'all' ? selectedSubCompany : undefined
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'injects_with_responses.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Full report exported (Injects, Logs, Messages, Calls)');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'status-live',
      planned: 'status-paused',
      completed: 'status-completed',
      disabled: 'status-draft',
      draft: 'status-draft'
    };
    return classes[status] || 'status-draft';
  };

  const filteredSubCompanies = selectedClient === 'all' 
    ? subCompanies 
    : subCompanies.filter(sc => sc.client_id === selectedClient);

  // Filter exercises based on selected client and sub-company
  const filteredExercises = exercises.filter(ex => {
    if (selectedClient !== 'all' && ex.client_id !== selectedClient) return false;
    if (selectedSubCompany !== 'all' && ex.sub_company_id !== selectedSubCompany) return false;
    return true;
  });

  return (
    <DashboardLayout title="Reports & Analytics">
      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-secondary text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Client</label>
                <Select value={selectedClient} onValueChange={(v) => {
                  setSelectedClient(v);
                  setSelectedSubCompany('all');
                  setSelectedExercise('all_exercises');
                }}>
                  <SelectTrigger>
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Sub-Company</label>
                <Select value={selectedSubCompany} onValueChange={(v) => {
                  setSelectedSubCompany(v);
                  setSelectedExercise('all_exercises');
                }}>
                  <SelectTrigger>
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Sub-Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sub-Companies</SelectItem>
                    {filteredSubCompanies.map(sc => (
                      <SelectItem key={sc.sub_company_id} value={sc.sub_company_id}>{sc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Exercise</label>
                <Select value={selectedExercise} onValueChange={setSelectedExercise}>
                  <SelectTrigger data-testid="report-exercise-select">
                    <Target className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Exercises" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_exercises">All Exercises</SelectItem>
                    {filteredExercises.map(ex => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Export</label>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleExportExercisesExcel} data-testid="export-exercises-excel">
                    <Download className="w-4 h-4 mr-1" />
                    Exercises
                  </Button>
                  <Button variant="default" size="sm" onClick={handleExportInjectsExcel} data-testid="export-injects-excel">
                    <Download className="w-4 h-4 mr-1" />
                    Full Report
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Full Report includes injects, responses, activity logs, messages &amp; call logs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-card border rounded-lg p-1">
            <TabsTrigger value="overview">Dashboard Overview</TabsTrigger>
            <TabsTrigger value="charts">Graphical Reports</TabsTrigger>
            <TabsTrigger value="exercise">Exercise Details</TabsTrigger>
          </TabsList>

          {/* Dashboard Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {hierarchicalData ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="w-8 h-8 text-purple-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Exercises</p>
                          <p className="font-secondary font-bold text-2xl">
                            {hierarchicalData.summary.total_exercises}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {hierarchicalData.summary.active_exercises} active
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Activity className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Total Injects</p>
                          <p className="font-secondary font-bold text-2xl">
                            {hierarchicalData.summary.total_injects}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {hierarchicalData.summary.triggered_injects} triggered
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-8 h-8 text-green-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Responses</p>
                          <p className="font-secondary font-bold text-2xl">
                            {hierarchicalData.summary.total_responses}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-8 h-8 text-orange-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Response Rate</p>
                          <p className="font-secondary font-bold text-2xl">
                            {hierarchicalData.summary.response_rate}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Status Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-secondary">Exercise Status Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(hierarchicalData.status_breakdown).map(([status, count]) => {
                          const total = hierarchicalData.summary.total_exercises || 1;
                          const percentage = (count / total) * 100;
                          return (
                            <div key={status} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="capitalize font-medium">{status}</span>
                                <span className="font-mono text-sm">{count} ({percentage.toFixed(1)}%)</span>
                              </div>
                              <Progress value={percentage} className="h-2" />
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="font-secondary">Inject Priority Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(hierarchicalData.priority_breakdown).map(([priority, count]) => {
                          const total = hierarchicalData.summary.total_injects || 1;
                          const percentage = (count / total) * 100;
                          const colors = { low: 'bg-green-500', medium: 'bg-yellow-500', high: 'bg-red-500' };
                          return (
                            <div key={priority} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="capitalize font-medium">{priority}</span>
                                <span className="font-mono text-sm">{count} ({percentage.toFixed(1)}%)</span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${colors[priority]}`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Exercises List */}
                {hierarchicalData.exercises?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-secondary">Exercises ({hierarchicalData.exercises.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {hierarchicalData.exercises.map((ex) => (
                          <div 
                            key={ex.exercise_id}
                            className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:border-primary/50 transition-colors"
                            onClick={() => {
                              setSelectedExercise(ex.exercise_id);
                              setActiveTab('exercise');
                            }}
                          >
                            <div>
                              <p className="font-medium">{ex.name}</p>
                              <p className="text-xs text-muted-foreground capitalize">{ex.exercise_type}</p>
                            </div>
                            <Badge className={`${getStatusBadgeClass(ex.status)} border capitalize`}>
                              {ex.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
          </TabsContent>

          {/* Graphical Reports Tab */}
          <TabsContent value="charts" className="space-y-6">
            {graphicalData ? (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="font-secondary text-lg">Graphical Reports</CardTitle>
                      <CardDescription>Charts adapt to the current filters</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={handleDownloadHierarchicalPdf}>
                        <FileText className="w-4 h-4 mr-2" />
                        PDF (Filters)
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPdfExercisesOpen((v) => !v)}>
                        <FileText className="w-4 h-4 mr-2" />
                        PDF (Select)
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* PDF Select Panel */}
                  {pdfExercisesOpen && (
                    <div className="mb-6 rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-sm font-medium">Select exercises for PDF</p>
                          <p className="text-xs text-muted-foreground">If none selected, the PDF uses the current filters.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              const ids = exercises.map((e) => e.exercise_id);
                              setPdfSelectedExerciseIds(ids);
                            }}
                            variant="secondary"
                          >
                            Select all
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setPdfSelectedExerciseIds([])}
                            variant="secondary"
                          >
                            Clear
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleDownloadHierarchicalPdf}
                          >
                            Download PDF
                          </Button>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
                        {exercises.map((ex) => {
                          const checked = pdfSelectedExerciseIds.includes(ex.exercise_id);
                          return (
                            <label
                              key={ex.exercise_id}
                              className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setPdfSelectedExerciseIds((prev) =>
                                    checked
                                      ? prev.filter((id) => id !== ex.exercise_id)
                                      : [...prev, ex.exercise_id]
                                  );
                                }}
                              />
                              <span className="truncate">{ex.name}</span>
                              <span className="ml-auto text-xs text-muted-foreground">{ex.status}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Exercise Status Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-secondary flex items-center gap-2">
                      <PieChart className="w-5 h-5" />
                      Exercise Status Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={graphicalData.statusPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {graphicalData.statusPieData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={STATUS_COLORS[entry.name.toLowerCase()] || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Inject Status Bar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-secondary flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Inject Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={graphicalData.injectStatusBarData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8">
                          {graphicalData.injectStatusBarData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={STATUS_COLORS[entry.name.toLowerCase()] || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Priority Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-secondary">Inject Priority Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={graphicalData.priorityBarData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count">
                          <Cell fill="#10b981" />
                          <Cell fill="#f59e0b" />
                          <Cell fill="#ef4444" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Response Timeline */}
                {graphicalData.responseTimelineData?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-secondary">Response Timeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={graphicalData.responseTimelineData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="responses" stroke="#8884d8" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Exercise Comparison */}
                {graphicalData.exerciseComparisonData?.length > 0 && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle className="font-secondary">Exercise Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={graphicalData.exerciseComparisonData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="injects" fill="#8884d8" name="Injects" />
                          <Bar dataKey="responses" fill="#82ca9d" name="Responses" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
          </TabsContent>

          {/* Exercise Details Tab */}
          <TabsContent value="exercise" className="space-y-6">
            {!selectedExercise || selectedExercise === 'all_exercises' ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Select an Exercise</p>
                  <p className="text-muted-foreground">Choose an exercise from the filter above to view its detailed report</p>
                </CardContent>
              </Card>
            ) : reportLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : reportData ? (
              <div className="space-y-6">
                {/* Exercise Header */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-secondary text-xl">
                          {reportData.exercise?.name}
                        </CardTitle>
                        <CardDescription>
                          {reportData.exercise?.exercise_type} Exercise • Generated {new Date(reportData.generated_at).toLocaleString()}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getStatusBadgeClass(reportData.exercise?.status)} border capitalize`}>
                          {reportData.exercise?.status}
                        </Badge>
                        <Button onClick={handleDownloadPdf} data-testid="download-pdf-btn">
                          <FileText className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Activity className="w-8 h-8 text-purple-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Total Injects</p>
                          <p className="font-secondary font-bold text-2xl">{reportData.injects?.length || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-8 h-8 text-green-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Responses</p>
                          <p className="font-secondary font-bold text-2xl">{reportData.stats?.response_count || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Response Rate</p>
                          <p className="font-secondary font-bold text-2xl">{reportData.stats?.response_rate || 0}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Clock className="w-8 h-8 text-orange-500" />
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">Timeline Events</p>
                          <p className="font-secondary font-bold text-2xl">{reportData.stats?.timeline_event_count || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Inject Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-secondary">Inject Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(reportData.stats?.inject_stats || {}).map(([status, count]) => {
                        const total = reportData.injects?.length || 1;
                        const percentage = (count / total) * 100;
                        return (
                          <div key={status} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="capitalize font-medium">{status}</span>
                              <span className="font-mono text-sm">{count} ({percentage.toFixed(1)}%)</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Participants */}
                {reportData.assigned_users?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-secondary">Participants ({reportData.assigned_users.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {reportData.assigned_users.map((user) => (
                          <div 
                            key={user.user_id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-border"
                          >
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              {user.picture ? (
                                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                              ) : (
                                <span className="font-semibold text-primary">
                                  {user.name?.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {user.roles?.join(', ')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p>No report data available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
